import { forwardRef, useEffect } from 'react';
import { useIsFetching, useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';
import { Icon } from './Icon';
import { fmtDateLong, today } from '../lib/format';

const TITLES: Record<string, string> = {
  '/dashboard': 'Executive Dashboard',
  '/transactions': 'Transactions',
  '/invoices': 'Invoices · Receivables',
  '/clients': 'Clients',
  '/accuracy': 'Forecast Accuracy',
  '/alerts': 'Alerts · Settings',
};

export interface TopbarProps {
  /** Whether the off-canvas nav is open (below the 900px nav breakpoint). */
  navOpen: boolean;
  onMenuClick: () => void;
}

export const Topbar = forwardRef<HTMLButtonElement, TopbarProps>(function Topbar(
  { navOpen, onMenuClick },
  menuRef,
) {
  const { pathname } = useLocation();
  const title = TITLES[pathname] ?? 'Fortalice';

  // Invalidate every React Query cache so each screen re-fetches against the
  // API on demand. `useIsFetching()` returns a live count of in-flight
  // queries; we spin the icon while it's non-zero.
  const qc = useQueryClient();
  const fetching = useIsFetching();
  const refresh = () => void qc.invalidateQueries();

  // Export PDF only makes sense on the Dashboard for now — print stylesheet
  // hides chrome and lays the dashboard out for landscape paper.
  const isDashboard = pathname === '/dashboard';
  const printPdf = () => window.print();

  // Force light mode for any print job: dark mode prints as a near-black page
  // with white tile cutouts, which wastes ink and looks wrong. Swap
  // data-theme to light before the browser renders the print view, then
  // restore the user's theme afterward. Covers both the Export PDF button and
  // a direct Cmd+P.
  useEffect(() => {
    const html = document.documentElement;
    let saved: string | null = null;
    const onBefore = () => {
      saved = html.getAttribute('data-theme');
      html.setAttribute('data-theme', 'light');
    };
    const onAfter = () => {
      if (saved !== null) html.setAttribute('data-theme', saved);
      else html.removeAttribute('data-theme');
    };
    window.addEventListener('beforeprint', onBefore);
    window.addEventListener('afterprint', onAfter);
    return () => {
      window.removeEventListener('beforeprint', onBefore);
      window.removeEventListener('afterprint', onAfter);
    };
  }, []);

  return (
    <header className="topbar no-print">
      <button
        ref={menuRef}
        className="icon-btn nav-toggle"
        aria-label={navOpen ? 'Close navigation' : 'Open navigation'}
        aria-expanded={navOpen}
        aria-controls="app-nav"
        onClick={onMenuClick}
      >
        <Icon name={navOpen ? 'close' : 'menu'} size={18} />
      </button>
      <h1>{title}</h1>
      <div className="topbar-meta">
        <span className="as-of">Snapshot {fmtDateLong(today())}</span>
        <button
          className="icon-btn"
          aria-label="Refresh data"
          title={fetching > 0 ? 'Refreshing…' : 'Refresh data'}
          onClick={refresh}
          disabled={fetching > 0}
        >
          <span className={fetching > 0 ? 'spin' : undefined}>
            <Icon name="refresh" size={16} />
          </span>
        </button>
        {isDashboard && (
          <button
            className="icon-btn"
            aria-label="Export dashboard as PDF"
            title="Export as PDF"
            onClick={printPdf}
          >
            <Icon name="download" size={16} />
          </button>
        )}
      </div>
    </header>
  );
});
